import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';

const NewSession = ({ goBack, goHome, navigateTo }) => {
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [questionHistory, setQuestionHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [userEmail, setUserEmail] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [emailMessage, setEmailMessage] = useState('');
    const [showRestrictionModal, setShowRestrictionModal] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const checkSession = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setShowRestrictionModal({ message: 'Please log in to start a session.', redirect: 'login' });
                return;
            }

            const { data: profile, error: profileError } = await supabase
                .schema('api')
                .from('user_profiles')
                .select('membership_type, session_complete, session_start_date, created_at, next_membership_start')
                .eq('id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError.message);
                setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'login' });
                return;
            }

            console.log('Debug: Profile data:', {
                membership_type: profile.membership_type,
                session_complete: profile.session_complete,
                session_start_date: profile.session_start_date,
                created_at: profile.created_at,
                next_membership_start: profile.next_membership_start
            });

            if (profile.membership_type === 'Free Subscription') {
                const now = new Date();
                console.log('Debug: Current UTC time:', now.toISOString());

                // 24-hour timeout check for incomplete sessions
                if (profile.session_start_date && !profile.session_complete) {
                    const sessionStart = new Date(profile.session_start_date + 'Z');
                    const hoursSinceStart = (now - sessionStart) / (1000 * 60 * 60);
                    console.log('Debug: Session timeout check:', {
                        session_start_date: profile.session_start_date,
                        parsed_session_start: sessionStart.toISOString(),
                        hoursSinceStart: hoursSinceStart.toFixed(2),
                        is_timeout: hoursSinceStart > 24
                    });
                    if (hoursSinceStart > 24) {
                        const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                            ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                            : new Date(profile.created_at);
                        const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                        const monthsSinceBase = Math.floor(daysSinceBase / 30);
                        const resetDate = new Date(baseDate);
                        resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);
                        setShowRestrictionModal({
                            message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                            redirect: 'back'
                        });
                        return;
                    }
                }

                // Monthly session limit check
                const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                    ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                    : new Date(profile.created_at);
                const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                const monthsSinceBase = Math.floor(daysSinceBase / 30);
                const resetDate = new Date(baseDate);
                resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);

                if (profile.session_complete && daysSinceBase < (monthsSinceBase + 1) * 30) {
                    console.log('Debug: Monthly limit reached, reset date:', resetDate.toLocaleDateString());
                    setShowRestrictionModal({
                        message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                        redirect: 'back'
                    });
                    return;
                }

                // Update session_start_date for new sessions
                if (!profile.session_complete && !profile.session_start_date) {
                    try {
                        const { error: updateError } = await supabase
                            .schema('api')
                            .from('user_profiles')
                            .update({
                                session_start_date: new Date().toISOString(),
                            })
                            .eq('id', user.id);

                        if (updateError) {
                            console.error('Error updating session_start_date:', updateError.message);
                            setShowRestrictionModal({ message: 'Failed to start the session. Please try again.', redirect: 'back' });
                            return;
                        }

                        console.log('Session start date updated successfully.');
                    } catch (err) {
                        console.error('Unexpected error updating session_start_date:', err);
                        setShowRestrictionModal({ message: 'An unexpected error occurred while starting the session.', redirect: 'back' });
                    }
                }
            }
        };

        const fetchInitial = async () => {
            console.log('Starting question fetch');
            setLoading(true);
            let didResume = false;
            try {
                const resumeQuestionId = localStorage.getItem('resumeQuestionId');
                if (resumeQuestionId && resumeQuestionId !== 'null') {
                    try {
                        const { data: savedQuestion, error: resumeError } = await supabase
                            .from('questions')
                            .select('*')
                            .eq('id', resumeQuestionId)
                            .single();
                        if (resumeError) {
                            console.error('Failed to load the resumed question:', resumeError.message);
                            setShowRestrictionModal({ message: 'Failed to load the resumed question: ' + resumeError.message, redirect: 'back' });
                        } else if (!savedQuestion) {
                            console.warn('No question found for the saved session.');
                            setShowRestrictionModal({ message: 'No question found for the saved session.', redirect: 'back' });
                        } else {
                            setCurrentQuestion(savedQuestion);
                            fetchAnswers(savedQuestion.id);
                            console.log('Loaded saved question:', savedQuestion);
                            didResume = true;
                        }
                    } catch (err) {
                        console.error('Unexpected error during resume:', err);
                        setShowRestrictionModal({ message: 'An unexpected error occurred: ' + err.message, redirect: 'back' });
                    } finally {
                        localStorage.removeItem('resumeQuestionId');
                    }
                }
                if (!didResume) {
                    const { data: question, error } = await supabase
                        .from('questions')
                        .select('*')
                        .eq('is_first', true)
                        .single();

                    if (error) {
                        console.error('Error fetching first question:', error.message);
                        setShowRestrictionModal({ message: 'Failed to load the first question.', redirect: 'back' });
                        return;
                    }

                    setCurrentQuestion(question);
                    fetchAnswers(question.id);
                    console.log('Loaded first question:', question);
                }
            } catch (err) {
                console.error('Unexpected error:', err);
                setShowRestrictionModal({ message: 'An unexpected error occurred.', redirect: 'back' });
            } finally {
                setLoading(false);
            }
        };

        const initialize = async () => {
            await checkSession();
            if (!showRestrictionModal) {
                await fetchInitial();
            }
        };
        initialize();
    }, [navigateTo, goBack, showRestrictionModal]);

    const fetchAnswers = async (questionId) => {
        try {
            const { data: answers, error } = await supabase
                .from('answers')
                .select('*')
                .eq('question_id', questionId);

            if (error) {
                console.error('Error fetching answers:', error.message);
                setShowRestrictionModal({ message: 'Failed to load answers.', redirect: 'back' });
                return;
            }

            console.log('Fetched Answers for', questionId, ':', answers);
            setAnswers(answers || []);
        } catch (err) {
            console.error('Unexpected error fetching answers:', err);
            setShowRestrictionModal({ message: 'An unexpected error occurred while fetching answers.', redirect: 'back' });
        }
    };

    const handleAnswerSubmit = async (answerText) => {
        if (!answerText) return;
        const answer = answers.find(a => a.text === answerText);
        if (!answer) {
            console.error('Answer not found:', answerText);
            setShowRestrictionModal({ message: 'Invalid answer selected.', redirect: 'back' });
            return;
        }

        if (answer.component) {
            console.log('Debug: Navigating to component:', answer.component);
            navigateTo(answer.component);
            return;
        }

        if (answer.next_question_id) {
            try {
                const { data: nextQuestion, error } = await supabase
                    .from('questions')
                    .select('*')
                    .eq('id', answer.next_question_id)
                    .single();

                if (error) {
                    console.error('Error fetching next question:', error.message);
                    setShowRestrictionModal({ message: 'Failed to load the next question.', redirect: 'back' });
                    return;
                }

                setQuestionHistory([...questionHistory, currentQuestion]);
                setCurrentQuestion(nextQuestion);
                fetchAnswers(nextQuestion.id);
                setSelectedAnswer('');
            } catch (err) {
                console.error('Unexpected error:', err);
                setShowRestrictionModal({ message: 'An unexpected error occurred.', redirect: 'back' });
            }
        } else {
            const { data: { user } } = await supabase.auth.getUser();
            const { data: profile, error: profileError } = await supabase
                .schema('api')
                .from('user_profiles')
                .select('session_count')
                .eq('id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError.message);
                setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'back' });
                return;
            }

            await supabase
                .schema('api')
                .from('user_profiles')
                .update({
                    session_complete: true,
                    session_count: profile.session_count + 1,
                    last_session_date: new Date().toISOString(),
                    completion_method: 'completed',
                })
                .eq('id', user.id);
            setShowRestrictionModal({ message: 'End of session!', redirect: 'endofSession' });
        }
    };

    const handleSaveProgress = async () => {
        try {
            console.log('Saving user progress...');
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                console.error('Error fetching user:', userError);
                alert('User not logged in! Please log in to save progress.');
                return;
            }

            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert({
                    user_id: user.id,
                    current_question_id: currentQuestion?.id,
                    current_component: 'newSession',
                    animated_helper: 'no', // always set to 'no'
                    timestamp: new Date().toISOString(),
                });

            if (progressError) {
                console.error('Error saving user progress:', progressError.message);
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            console.log('User progress saved successfully!');
            alert('Session progress has been saved successfully!');
        } catch (err) {
            console.error('Unexpected error saving progress:', err);
            alert('An unexpected error occurred while saving progress.');
        }
    };

    const handleBack = async () => {
        if (questionHistory.length > 0) {
            const newHistory = [...questionHistory];
            const previousQuestion = newHistory.pop();
            setCurrentQuestion(previousQuestion);
            fetchAnswers(previousQuestion.id);
            setQuestionHistory(newHistory);
            setSelectedAnswer('');
        } else {
            console.log('Debug: Navigating back');
            goBack();
        }
    };

    // Render modal if restriction or error occurs
    if (showRestrictionModal) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    maxWidth: window.innerWidth <= 600 ? '90vw' : '400px',
                    width: '100%',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid #4B5563',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                    color: '#4B5563',
                }}>
                    <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>{showRestrictionModal.message}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        {showRestrictionModal.redirect === 'back' && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px', // Increased width
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking Back button');
                                    setShowRestrictionModal(null);
                                    goBack();
                                }}
                            >
                                Back
                            </button>
                        )}
                        {(showRestrictionModal.redirect === 'back' || showRestrictionModal.redirect === null) && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px', // Increased width
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking My Account button, navigating to myaccount');
                                    setShowRestrictionModal(null);
                                    navigateTo('myAccount');
                                }}
                            >
                                My Account
                            </button>
                        )}
                        {showRestrictionModal.redirect && showRestrictionModal.redirect !== 'back' && showRestrictionModal.redirect !== null && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px', // Increased width
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking OK button, navigating to', showRestrictionModal.redirect);
                                    setShowRestrictionModal(null);
                                    navigateTo(showRestrictionModal.redirect);
                                }}
                            >
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (loading) return <div>Loading...</div>;
    if (!currentQuestion) return <div>No question found.</div>;

    return (
        <div style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <div
                style={{
                    maxWidth: window.innerWidth <= 600 ? '98vw' : '560px',
                    width: window.innerWidth <= 600 ? '98vw' : '560px',
                    border: '2px solid #4B5563',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: window.innerWidth <= 600 ? '8px auto' : '16px auto',
                    padding: 0,
                    background: '#fff',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        height: window.innerWidth <= 600 ? '120px' : '192px',
                        backgroundImage: currentQuestion.image ? `url(/images/${currentQuestion.image.replace(/\.(jpg|jpeg|png)$/i, '.webp')})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'bottom',
                        border: '2px solid #4B5563',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        borderBottom: 'none',
                        margin: 0,
                        padding: 0,
                        boxSizing: 'border-box',
                    }}
                ></div>
                <div style={{
                    width: '100%',
                    height: window.innerWidth <= 600 ? 'auto' : '188px',
                    minHeight: window.innerWidth <= 600 ? '120px' : undefined,
                    padding: '8px',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: '8px',
                    borderTop: 'none',
                    margin: 0,
                    boxSizing: 'border-box',
                }}>
                    <p style={{
                        marginTop: '8px',
                        color: '#4B5563',
                        textAlign: 'center',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                        fontWeight: 'bold',
                    }}>
                        {currentQuestion.text}
                    </p>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: window.innerWidth <= 600 ? '4px' : '10px',
                        width: '100%',
                        justifyContent: 'center'
                    }}>
                        <select
                            style={{
                                border: '2px solid black',
                                borderRadius: '10px',
                                padding: window.innerWidth <= 600 ? '4px' : '5px',
                                width: window.innerWidth <= 600 ? '90vw' : '340px',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                                fontWeight: 'bold',
                                maxWidth: '100%',
                            }}
                            value={selectedAnswer}
                            onChange={(e) => handleAnswerSubmit(e.target.value)}
                        >
                            <option value="">Select an answer</option>
                            {answers.map((answer) => (
                                <option key={answer.id} value={answer.text}>
                                    {answer.text}
                                </option>
                            ))}
                        </select>
                    </div>
                    <p style={{
                        marginTop: '8px',
                        color: '#4B5563',
                        textAlign: 'center',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                        fontWeight: 'bold',
                    }}>
                        Drop down menu to choose response
                    </p>
                </div>
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: window.innerWidth <= 600 ? '6px' : '16px',
                marginTop: window.innerWidth <= 600 ? '18px' : '56px',
                width: window.innerWidth <= 600 ? '98vw' : undefined,
            }}>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                        width: window.innerWidth <= 600 ? '32vw' : '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: window.innerWidth <= 600 ? '15px' : undefined,
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleBack}
                >
                    Back
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                        width: window.innerWidth <= 600 ? '32vw' : '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: window.innerWidth <= 600 ? '15px' : undefined,
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={goHome}
                >
                    Home
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                        width: window.innerWidth <= 600 ? '32vw' : '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: window.innerWidth <= 600 ? '15px' : undefined,
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleSaveProgress}
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default NewSession;